import { Drawer, DrawerProps, Select } from 'antd';
import { getContextsRegions, useGetTitle } from './share';
import { useEffect, useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import { ScenarioResult } from 'views/Setting/AutoEmit/components/Result';
import styled from '@emotion/styled';
import { difference } from 'lodash-es';
import { useGetResultList } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import { getSlateContent } from 'views/Aide/Scenario/PolicyForm';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface Active {
  activitySendPoint: Knowledge.Policy.ActivitySendPoint;
  scenarioType: Knowledge.Policy.ScenarioType;
  region: General.UpperCaseRegion;
  platforms: General.PlatformUpperCase[];
  itemHighlight?: string;
}

export interface PolicyPreviewProps extends DrawerProps {
  activity: Knowledge.Activity.Detail;
  allPlatforms: General.PlatformUpperCase[];
  contexts: Knowledge.Policy.KnowledgeScenarioPolicy[];
  active: Active;
  onActiveChange: (active: Active) => void;
}

function PolicyPreview(props: PolicyPreviewProps) {
  const { activity, allPlatforms, contexts, active, onActiveChange, ...otherProps } = props;

  const { activitySendPoint, scenarioType, region, platforms, itemHighlight = '' } = active;

  const onUpdate = useMemoizedFn((partial: Partial<Active>) =>
    onActiveChange({
      ...active,
      ...partial,
    }),
  );

  const { t, getTitle } = useGetTitle();
  const getRegionLabel = useGetRegionLabel();

  const regions = useMemo(() => getContextsRegions(contexts), [contexts]);

  const regionSelect = useMemo(() => {
    return (
      <Select
        dropdownMatchSelectWidth={false}
        value={region}
        onChange={(region) => onUpdate({ region })}
        options={regions.map((region) => {
          return {
            value: region,
            label: getRegionLabel(region),
          };
        })}
      />
    );
  }, [regions, region, onUpdate, getRegionLabel]);

  const scenarioSelect = useMemo(() => {
    const valueMap = contexts.reduce<
      Record<string, Pick<Active, 'activitySendPoint' | 'scenarioType' | 'platforms'>>
    >((res, cur) => {
      const { activitySendPoint, scenarioType, regionPolicyList } = cur;

      return regionPolicyList.reduce((res, { platformPolicyList }) => {
        return platformPolicyList.reduce((res, { platforms }) => {
          res[`${activitySendPoint}@${scenarioType}@${platforms.join('@')}`] = {
            activitySendPoint,
            scenarioType,
            platforms,
          };

          return res;
        }, res);
      }, res);
    }, {});

    return (
      <Select
        dropdownMatchSelectWidth={false}
        value={`${activitySendPoint}@${scenarioType}@${platforms.join('@')}`}
        onChange={(value) => {
          const { [value]: active } = valueMap;

          if (active) {
            onUpdate(active);
          }
        }}
        options={Object.keys(valueMap).map((key) => {
          const { activitySendPoint, scenarioType, platforms } = valueMap[key];
          return {
            value: key,
            label:
              getTitle(activitySendPoint, scenarioType, activity) +
              (difference(allPlatforms, platforms).length === 0 ? '' : `-${platforms.join('/')}`),
          };
        })}
      />
    );
  }, [
    contexts,
    activitySendPoint,
    scenarioType,
    platforms,
    allPlatforms,
    activity,
    onUpdate,
    getTitle,
  ]);

  const highlights = useMemo(() => {
    return contexts.reduce<string[]>((res, context) => {
      if (
        context.activitySendPoint !== activitySendPoint ||
        context.scenarioType !== scenarioType
      ) {
        return res;
      }
      return context.regionPolicyList.reduce((res, regionPolicy) => {
        if (regionPolicy.region !== region) {
          return res;
        }
        return regionPolicy.platformPolicyList.reduce((res, { itemHighlight = '' }) => {
          if (!res.includes(itemHighlight)) {
            res.push(itemHighlight);
          }
          return res;
        }, res);
      }, res);
    }, []);
  }, [contexts, activitySendPoint, scenarioType, region]);

  useEffect(() => {
    if (highlights.length > 0 && !highlights.includes(itemHighlight)) {
      onUpdate({
        itemHighlight: highlights[0],
      });
    }
  }, [highlights, itemHighlight, onUpdate]);

  const highlightSelect = useMemo(() => {
    if (highlights.length <= 1) {
      return null;
    }

    return (
      <Select
        dropdownMatchSelectWidth={false}
        value={itemHighlight}
        onChange={(itemHighlight) => onUpdate({ itemHighlight })}
        options={highlights.map((value) => ({ value, label: value || t('活动基础话术') }))}
      />
    );
  }, [highlights, itemHighlight, onUpdate, t]);

  const content = useMemo(() => {
    const scenarioTarget = contexts.find(
      (_) => _.activitySendPoint === activitySendPoint && _.scenarioType === scenarioType,
    );

    if (!scenarioTarget) {
      return;
    }

    const policyTarget = scenarioTarget.regionPolicyList.find((_) => _.region === region);

    if (!policyTarget) {
      return;
    }

    const platformTarget = policyTarget.platformPolicyList.find(
      (_) =>
        difference(_.platforms, platforms).length === 0 &&
        (_.itemHighlight || '') === itemHighlight,
    );

    if (!platformTarget) {
      return;
    }

    return platformTarget.executeContext.content;
  }, [contexts, activitySendPoint, scenarioType, region, platforms, itemHighlight]);

  const getResultList = useGetResultList({
    platform: platforms.length === 1 ? platforms[0] : undefined,
    region,
    systemContent: getTitle(activitySendPoint, scenarioType, activity),
  });

  const preview = useMemo(
    () =>
      content ? (
        <ScenarioResult
          list={getResultList({
            content: getSlateContent(content),
            translatedContent: getSlateContent(''),
          })}
        />
      ) : null,
    [content, getResultList],
  );

  return (
    <Drawer title={t('view_effect')} width={438} {...otherProps}>
      <SelectWrapper>
        {regionSelect}
        {scenarioSelect}
        {highlightSelect}
      </SelectWrapper>
      <PreviewWrapper>{preview}</PreviewWrapper>
    </Drawer>
  );
}

const SelectWrapper = styled.div`
  > * {
    margin-bottom: 12px;
    &:not(:last-of-type) {
      margin-right: 12px;
    }
  }
`;

const PreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export default PolicyPreview;
